import type { ReactNode } from 'react'
import { memo, useMemo, useRef } from 'react'
import type { Status } from './availability'

// Shared SVG map surface for both the USA (51 shapes) and Global (175 shapes)
// tabs. Two deliberate performance choices, which matter because the world map
// has 3.4x the shape count of the US map and this widget runs inside partner
// pages we don't control:
//
//  1. Pointer events are DELEGATED to the <svg> instead of bound per shape, so
//     switching tabs doesn't allocate 175 handler closures and hovering does
//     not walk them.
//  2. The shape layer is memoized on a precomputed array and never re-renders
//     on hover. The pinned highlight is drawn as a single overlay <path> on top
//     (pointer-events: none, so it stays transparent to hit-testing) rather
//     than by toggling a class inside the 175-node set, which would re-render
//     the whole layer on every cursor movement.

export interface MapShape {
  id: string
  name: string
  d: string
  /**
   * Optional popover anchor in viewBox units. Supplied by the generated world
   * data as the centroid of the country's largest landmass. Without it the
   * anchor falls back to the element's bounding-box centre, which is wrong for
   * shapes split across the antimeridian — Russia's and Fiji's boxes span
   * nearly the whole map and centre in open ocean.
   */
  cx?: number
  cy?: number
}

interface RenderShape extends MapShape {
  cls: string
}

const ShapeLayer = memo(({ shapes }: { shapes: RenderShape[] }) => (
  <g>
    {shapes.map(s => (
      <path key={s.id} d={s.d} data-id={s.id} className={s.cls} aria-label={s.name} />
    ))}
  </g>
))
ShapeLayer.displayName = 'ShapeLayer'

interface Props {
  viewBox: string
  shapes: MapShape[]
  statusFor: (id: string) => Status
  activeId: string | null
  /** Called with the hovered/tapped shape id and its anchor point in viewBox units. */
  onOpen: (id: string, point: { x: number, y: number }, svgRect: DOMRect) => void
  onToggle: (id: string, point: { x: number, y: number }, svgRect: DOMRect) => void
  onScheduleClose: () => void
  onCancelClose: () => void
  /** Clicking the map background (not a shape) — used to dismiss in click mode. */
  onBackgroundClick: () => void
  /**
   * How the popover opens.
   *
   * 'hover' — the USA map's long-standing behaviour, 51 large shapes.
   * 'click' — the Global map. At 176 countries the shapes are small and dense,
   *   so hover-to-open fires constantly while the cursor crosses the map and the
   *   panel flickers between neighbours. Click also matches how the map already
   *   had to work on touch.
   */
  openOn: 'hover' | 'click'
  ariaLabel: string
  /** Thinner strokes for the world map — country borders are much denser. */
  variant: 'us' | 'world'
  /**
   * The popover. Rendered as a sibling of the <svg> inside the positioned
   * wrapper, not inside it — it is HTML, which cannot be a child of <svg>
   * without a foreignObject.
   */
  children?: ReactNode
}

export default function MapSurface({
  viewBox,
  shapes,
  statusFor,
  activeId,
  onOpen,
  onToggle,
  onScheduleClose,
  onCancelClose,
  onBackgroundClick,
  openOn,
  ariaLabel,
  variant,
  children,
}: Props) {
  const svgRef = useRef<SVGSVGElement>(null)

  const renderShapes = useMemo<RenderShape[]>(
    () => shapes.map(s => ({ ...s, cls: `am-s-${statusFor(s.id)}` })),
    [shapes, statusFor],
  )

  const byId = useMemo(() => new Map(shapes.map(s => [s.id, s])), [shapes])
  const active = activeId ? byId.get(activeId) ?? null : null
  const activeCls = activeId ? `am-s-${statusFor(activeId)}` : ''

  // Resolves the shape under the pointer from a delegated event. Identified by
  // tag + data-id rather than `instanceof SVGPathElement`: that global is not
  // implemented by every DOM (jsdom omits it, which made this path untestable),
  // and the overlay path carries no data-id so it is skipped either way.
  const resolve = (target: EventTarget | null): { id: string, path: SVGGraphicsElement } | null => {
    const el = target as SVGGraphicsElement | null
    if (!el || el.tagName !== 'path') return null
    const id = el.dataset?.id
    if (!id) return null
    return { id, path: el }
  }

  const emit = (
    target: EventTarget | null,
    fn: (id: string, point: { x: number, y: number }, rect: DOMRect) => void,
  ) => {
    const hit = resolve(target)
    const svg = svgRef.current
    if (!hit || !svg) return
    const shape = byId.get(hit.id)
    // Precomputed anchor when the dataset carries one — also avoids a getBBox()
    // layout flush on every hover.
    const point = shape?.cx !== undefined && shape.cy !== undefined
      ? { x: shape.cx, y: shape.cy }
      : (() => {
          const b = hit.path.getBBox()
          return { x: b.x + b.width / 2, y: b.y + b.height / 2 }
        })()
    fn(hit.id, point, svg.getBoundingClientRect())
  }

  return (
    <div className="am-map-wrap">
      <svg
        className={`am-map am-map-${variant}`}
        ref={svgRef}
        viewBox={viewBox}
        role="img"
        aria-label={ariaLabel}
        onMouseOver={openOn === 'hover'
          ? (e) => {
              onCancelClose()
              emit(e.target, onOpen)
            }
          : undefined}
        onMouseOut={openOn === 'hover'
          ? (e) => {
              // Leaving one shape for another fires out-then-over; the close is
              // scheduled with a grace period so the popover doesn't flicker.
              if (resolve(e.target)) onScheduleClose()
            }
          : undefined}
        onClick={(e) => {
          // A click landing on the svg itself rather than a country is a
          // dismiss — the only way out of click mode besides re-clicking the
          // same country or pressing Escape.
          if (!resolve(e.target)) {
            onBackgroundClick()
            return
          }
          emit(e.target, onToggle)
        }}
      >
        <ShapeLayer shapes={renderShapes} />
        {active && (
          <path className={`am-on-overlay ${activeCls}`} d={active.d} aria-hidden="true" />
        )}
      </svg>
      {children}
    </div>
  )
}
